import React from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@klear/ui/components/card'
import { Input } from '@klear/ui/components/input'
import { Label } from '@klear/ui/components/label'
import { Checkbox } from '@klear/ui/components/checkbox'
import { Combobox } from '@klear/ui/components/combobox'
import { COUNTRY_CODE_OPTIONS } from '@klear/constants/countries'
import {
  PAYMENT_NETWORK_ACH,
  PAYMENT_NETWORK_WIRE,
  type BankPaymentNetwork,
  type ExternalBankAccountHolderType,
  type ExternalBankAccountType
} from '@klear/bank-account/constants'
import { cn } from '@klear/ui/lib/utils'
import union from 'lodash/union'
import without from 'lodash/without'

export type ExternalBankAccountAddressFormShape = {
  line1: string
  line2: string
  city: string
  state: string
  postal_code: string
  country: string
}

export type ExternalBankAccountFormShape = {
  nickname: string
  accountHolderType: ExternalBankAccountHolderType
  accountType: ExternalBankAccountType
  accountNumber: string
  routingNumber: string
  recipientName: string
  recipientEmail: string
  recipientPhone: string
  address: ExternalBankAccountAddressFormShape
  supportedPaymentNetworks: BankPaymentNetwork[]
}

type AddressKey = keyof ExternalBankAccountAddressFormShape
type TopLevelKey = Exclude<keyof ExternalBankAccountFormShape, 'address'>

export type ExternalBankAccountFieldPath = TopLevelKey | `address.${AddressKey}`
export type ExternalBankAccountFieldValue =
  | ExternalBankAccountFormShape[TopLevelKey]
  | ExternalBankAccountAddressFormShape[AddressKey]
export type ExternalBankAccountErrorMap = Record<string, string | undefined>

export interface ExternalBankAccountFieldProps {
  values: ExternalBankAccountFormShape
  errors: ExternalBankAccountErrorMap
  onFieldChange: (path: ExternalBankAccountFieldPath, value: ExternalBankAccountFieldValue) => void
  isEditing: boolean
}

function FieldShell({
  label,
  htmlFor,
  error,
  helperText,
  className,
  children
}: {
  label: string
  htmlFor?: string
  error?: string
  helperText?: string
  className?: string
  children: React.ReactNode
}) {
  return (
    <div className={cn('space-y-2', className)}>
      <Label htmlFor={htmlFor}>{label}</Label>
      {children}
      {error ? <p className="text-sm text-destructive">{error}</p> : null}
      {helperText ? <p className="text-sm text-muted-foreground">{helperText}</p> : null}
    </div>
  )
}

function TextField({
  path,
  label,
  value,
  placeholder,
  errors,
  onFieldChange,
  disabled,
  className,
  type = 'text'
}: {
  path: ExternalBankAccountFieldPath
  label: string
  value: string
  placeholder?: string
  errors: ExternalBankAccountErrorMap
  onFieldChange: ExternalBankAccountFieldProps['onFieldChange']
  disabled?: boolean
  className?: string
  type?: string
}) {
  const error = errors[path]
  return (
    <FieldShell label={label} htmlFor={path} error={error} className={className}>
      <Input
        id={path}
        type={type}
        value={value}
        placeholder={placeholder}
        disabled={disabled}
        className={error ? 'border-destructive' : ''}
        onChange={(e) => onFieldChange(path, e.target.value)}
      />
    </FieldShell>
  )
}

export function AccountFields({
  values,
  errors,
  onFieldChange,
  isEditing,
  isAccountLocked
}: ExternalBankAccountFieldProps & { isAccountLocked: boolean }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Bank account</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <TextField
          path="nickname"
          label="Nickname"
          value={values.nickname}
          placeholder="e.g. Operating account"
          errors={errors}
          onFieldChange={onFieldChange}
          disabled={!isEditing}
        />

        <div className="space-y-2">
          <Label>Supported networks</Label>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <Checkbox
                checked={values.supportedPaymentNetworks.includes(PAYMENT_NETWORK_ACH)}
                disabled={!isEditing}
                onCheckedChange={(checked) => {
                  const next = checked
                    ? union(values.supportedPaymentNetworks, [PAYMENT_NETWORK_ACH])
                    : without(values.supportedPaymentNetworks, PAYMENT_NETWORK_ACH)
                  onFieldChange('supportedPaymentNetworks', next)
                }}
              />
              <Label>ACH</Label>
            </div>
            <div className="flex items-center gap-2">
              <Checkbox
                checked={values.supportedPaymentNetworks.includes(PAYMENT_NETWORK_WIRE)}
                disabled={!isEditing}
                onCheckedChange={(checked) => {
                  const next = checked
                    ? union(values.supportedPaymentNetworks, [PAYMENT_NETWORK_WIRE])
                    : without(values.supportedPaymentNetworks, PAYMENT_NETWORK_WIRE)
                  onFieldChange('supportedPaymentNetworks', next)
                }}
              />
              <Label>Wire</Label>
            </div>
          </div>
          {errors.supportedPaymentNetworks ? (
            <p className="text-sm text-destructive">{errors.supportedPaymentNetworks}</p>
          ) : null}
        </div>

        <div className="grid grid-cols-2 gap-4">
          <FieldShell label="Account holder type" htmlFor="accountHolderType" error={errors.accountHolderType}>
            <Combobox<string>
              value={values.accountHolderType}
              valueType="string"
              placeholder="Select"
              disabled={!isEditing || isAccountLocked}
              options={[
                { label: 'Individual', value: 'individual' },
                { label: 'Company', value: 'company' }
              ]}
              onChange={(v) => onFieldChange('accountHolderType', v as ExternalBankAccountFieldValue)}
              className="w-full"
            />
          </FieldShell>
          <FieldShell label="Account type" htmlFor="accountType" error={errors.accountType}>
            <Combobox<string>
              value={values.accountType}
              valueType="string"
              placeholder="Select"
              disabled={!isEditing || isAccountLocked}
              options={[
                { label: 'Checking', value: 'checking' },
                { label: 'Savings', value: 'savings' }
              ]}
              onChange={(v) => onFieldChange('accountType', v as ExternalBankAccountFieldValue)}
              className="w-full"
            />
          </FieldShell>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <TextField
            path="accountNumber"
            label="Account number"
            value={values.accountNumber}
            placeholder="Account number"
            errors={errors}
            onFieldChange={onFieldChange}
            disabled={!isEditing || isAccountLocked}
          />
          <TextField
            path="routingNumber"
            label="Routing number"
            value={values.routingNumber}
            placeholder="9-digit routing number"
            errors={errors}
            onFieldChange={onFieldChange}
            disabled={!isEditing || isAccountLocked}
          />
        </div>
      </CardContent>
    </Card>
  )
}

export function HolderFields({ values, errors, onFieldChange, isEditing }: ExternalBankAccountFieldProps) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Holder</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <TextField
          path="recipientName"
          label="Recipient name"
          value={values.recipientName}
          placeholder="Full legal name"
          errors={errors}
          onFieldChange={onFieldChange}
          disabled={!isEditing}
        />
        <TextField
          path="recipientEmail"
          label="Recipient email"
          value={values.recipientEmail}
          placeholder="name@example.com"
          errors={errors}
          onFieldChange={onFieldChange}
          disabled={!isEditing}
        />
        <TextField
          path="recipientPhone"
          label="Recipient phone"
          value={values.recipientPhone}
          placeholder="555-555-5555"
          errors={errors}
          onFieldChange={onFieldChange}
          disabled={!isEditing}
        />
      </CardContent>
    </Card>
  )
}

export function AddressFields({ values, errors, onFieldChange, isEditing }: ExternalBankAccountFieldProps) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Address</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <TextField
            path="address.line1"
            label="Address line 1"
            value={values.address.line1}
            placeholder="Street address"
            errors={errors}
            onFieldChange={onFieldChange}
            disabled={!isEditing}
          />
          <TextField
            path="address.line2"
            label="Address line 2"
            value={values.address.line2}
            placeholder="Apt, suite, etc. (optional)"
            errors={errors}
            onFieldChange={onFieldChange}
            disabled={!isEditing}
          />
        </div>
        <div className="grid grid-cols-12 gap-4">
          <TextField
            path="address.city"
            label="City"
            value={values.address.city}
            placeholder="City"
            errors={errors}
            onFieldChange={onFieldChange}
            disabled={!isEditing}
            className="col-span-6"
          />
          <TextField
            path="address.state"
            label="State"
            value={values.address.state}
            placeholder="State"
            errors={errors}
            onFieldChange={onFieldChange}
            disabled={!isEditing}
            className="col-span-2"
          />
          <TextField
            path="address.postal_code"
            label="Postal code"
            value={values.address.postal_code}
            placeholder="ZIP / postal code"
            errors={errors}
            onFieldChange={onFieldChange}
            disabled={!isEditing}
            className="col-span-4"
          />
        </div>
        <FieldShell label="Country" htmlFor="address.country" error={errors['address.country']}>
          <Combobox<string>
            value={values.address.country}
            valueType="string"
            placeholder="Select country"
            disabled={!isEditing}
            options={COUNTRY_CODE_OPTIONS}
            onChange={(v) => onFieldChange('address.country', v as ExternalBankAccountFieldValue)}
            className="w-full"
          />
        </FieldShell>
      </CardContent>
    </Card>
  )
}
